const API_URL = process.env.GATSBY_SPASEEKERS_ANALYTICS_API_URL;
const TAGGED_COOKIES = (process.env.GATSBY_SPASEEKERS_ANALYTICS_TAGGED_COOKIES || '')
  .split(',')
  .map(x => x.trim())
  .filter(Boolean);

const SESSION_COOKIE_NAME = 'spa_session_id';
const SESSION_TTL_MS = 24 * 60 * 60 * 1000;
const ULID_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const MAX_ATTEMPTS = 3;
const REQUEST_TIMEOUT_MS = 4000;
const BOT_UA_HINTS = (
  process.env.GATSBY_SPASEEKERS_ANALYTICS_BOT_UA_HINTS || ''
)
  .split(',')
  .map(x => x.trim().toLowerCase())
  .filter(Boolean);

// Broad bot/crawler UA signals plus common vendors.
const KNOWN_BOT_UA_REGEX =
  /(bot\b|crawler|spider|slurp|bingpreview|mediapartners-google|adsbot-google|googleother|apis-google|lighthouse|pagespeed|headlesschrome|prerender|facebookexternalhit|meta-externalagent|whatsapp|slackbot|discordbot|telegrambot|twitterbot|linkedinbot|pinterest|applebot|yandex|baiduspider|duckduckbot|petalbot|semrush|ahrefs|mj12bot|dotbot|screaming frog|uptimerobot|pingdom|statuscake|newrelicpinger|datadog\/synthetics|syntheticmonitors)/i;

let cachedBotDetectionResult;

function safeDebugLog(...args) {
  try {
    if (typeof console !== 'undefined' && typeof console.debug === 'function') {
      console.debug(...args);
    }
  } catch (_error) {
    // Never let logging impact page execution
  }
}

function isBrowser() {
  return typeof window !== 'undefined';
}

function getUserAgent() {
  if (!isBrowser() || typeof navigator === 'undefined') {
    return '';
  }

  return navigator.userAgent || '';
}

function isKnownBotAgent(userAgent = getUserAgent()) {
  if (!userAgent) {
    return false;
  }

  const ua = userAgent.toLowerCase();

  if (KNOWN_BOT_UA_REGEX.test(ua)) {
    return true;
  }

  return BOT_UA_HINTS.some(hint => ua.includes(hint));
}

function shouldSkipAnalyticsForAgent() {
  if (cachedBotDetectionResult !== undefined) {
    return cachedBotDetectionResult;
  }

  try {
    cachedBotDetectionResult = isKnownBotAgent();
  } catch (error) {
    cachedBotDetectionResult = false;
    safeDebugLog('[analytics] bot detection failed', error);
  }

  return cachedBotDetectionResult;
}

function randomInt(maxExclusive) {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  ) {
    const arr = new Uint32Array(1);
    crypto.getRandomValues(arr);
    return arr[0] % maxExclusive;
  }

  return Math.floor(Math.random() * maxExclusive);
}

function encodeTime(timestamp, len) {
  let value = timestamp;
  let result = '';

  for (let i = 0; i < len; i += 1) {
    result = ULID_ALPHABET.charAt(value % 32) + result;
    value = Math.floor(value / 32);
  }

  return result;
}

function encodeRandom(len) {
  let result = '';

  for (let i = 0; i < len; i += 1) {
    result += ULID_ALPHABET.charAt(randomInt(32));
  }

  return result;
}

function generateUlid() {
  return `${encodeTime(Date.now(), 10)}${encodeRandom(16)}`;
}

function sleep(ms) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

function isTransientStatus(status) {
  return status === 408 || status === 425 || status === 429 || status >= 500;
}

function normaliseError(error) {
  if (error instanceof Error) {
    return {
      message: error.message,
      name: error.name,
      stack: error.stack,
    };
  }

  if (typeof error === 'string') {
    return { message: error };
  }

  return { message: 'Unknown error' };
}

function sanitiseTags(tags = {}) {
  const output = {};

  Object.keys(tags).forEach(key => {
    const value = tags[key];
    if (value === null || value === undefined) {
      return;
    }

    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      output[key] = value;
      return;
    }

    output[key] = String(value);
  });

  return output;
}

function getDayPartition(timestampUtc) {
  return timestampUtc.slice(0, 10).replace(/-/g, '');
}

function parseCookies() {
  if (!isBrowser() || typeof document === 'undefined') {
    return {};
  }

  const cookieMap = {};
  const rawCookies = document.cookie ? document.cookie.split(';') : [];

  rawCookies.forEach(cookieEntry => {
    try {
      const separatorIndex = cookieEntry.indexOf('=');
      if (separatorIndex < 0) {
        return;
      }

      const rawName = cookieEntry.slice(0, separatorIndex).trim();
      const rawValue = cookieEntry.slice(separatorIndex + 1).trim();

      if (!rawName) {
        return;
      }

      cookieMap[decodeURIComponent(rawName)] = decodeURIComponent(rawValue);
    } catch (error) {
      safeDebugLog('[analytics] failed to parse cookie', error);
    }
  });

  return cookieMap;
}

function setSessionCookie(name, value, maxAgeMs) {
  if (!isBrowser() || typeof document === 'undefined') {
    return;
  }

  try {
    const maxAge = Math.floor(maxAgeMs / 1000);
    const cookieValue = encodeURIComponent(value);
    document.cookie = `${name}=${cookieValue}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
  } catch (_error) {
    // Cookie setting failed, silently continue
  }
}

export function getOrCreateSessionId() {
  if (!isBrowser()) {
    return generateUlid();
  }

  try {
    const cookies = parseCookies();
    const existing = cookies[SESSION_COOKIE_NAME];

    if (existing) {
      // Refresh the cookie expiration
      setSessionCookie(SESSION_COOKIE_NAME, existing, SESSION_TTL_MS);
      return existing;
    }

    const sessionId = generateUlid();
    setSessionCookie(SESSION_COOKIE_NAME, sessionId, SESSION_TTL_MS);
    return sessionId;
  } catch (_error) {
    return generateUlid();
  }
}

export function getCookieTags() {
  const allCookies = parseCookies();
  const tags = {};

  TAGGED_COOKIES.forEach(cookieName => {
    const value = allCookies[cookieName];
    if (value !== undefined && value !== null && value !== '') {
      tags[cookieName] = value;
    }
  });

  return tags;
}

async function fetchWithTimeout(url, body) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, REQUEST_TIMEOUT_MS);

  try {
    return await fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

async function postWithRetry(url, body) {
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    try {
      const response = await fetchWithTimeout(url, body);

      if (response.ok) {
        return;
      }

      if (!isTransientStatus(response.status) || attempt === MAX_ATTEMPTS) {
        safeDebugLog('[analytics] request failed', {
          status: response.status,
          statusText: response.statusText,
        });
        return;
      }
    } catch (error) {
      if (attempt === MAX_ATTEMPTS) {
        safeDebugLog('[analytics] request failed', error);
        return;
      }
    }

    const backoffMs = 200 * Math.pow(2, attempt - 1);
    await sleep(backoffMs);
  }
}

export async function sendAnalyticsEvent(eventType, payload, tags = {}) {
  try {
    if (!isBrowser() || !API_URL || shouldSkipAnalyticsForAgent()) {
      return;
    }

    const sessionId = getOrCreateSessionId();
    const timestampUtc = new Date().toISOString();
    const cookieTags = getCookieTags();
    const arbitraryTags = sanitiseTags(tags);
    const finalTags = {
      ...cookieTags,
      ...arbitraryTags,
    };

    const requestBody = {
      eventType,
      payloadJson: JSON.stringify(payload),
      sessionId,
      timestampUtc,
      dayPartition: getDayPartition(timestampUtc),
      tagsJson: JSON.stringify(finalTags),
    };

    await postWithRetry(`${API_URL}/event`, requestBody);
  } catch (error) {
    safeDebugLog('[analytics] sendAnalyticsEvent failed', error);
  }
}

export function trackPageView(details, tags = {}) {
  void sendAnalyticsEvent('page', details, tags);
}

export function trackBasket(tags = {}) {
  void sendAnalyticsEvent('basket', {}, tags);
}

export function trackBasketUpdate(details = {},tags = {}) {
  void sendAnalyticsEvent('basket.update', details, tags);
}

export function trackConfiguration(details = {}, tags = {}) {
  void sendAnalyticsEvent('configuration', details, tags);
}

export function trackBasketAdd(details = {}, tags = {}) {
  void sendAnalyticsEvent('basket.add', details, tags);
}

export function trackBasketRemove(details = {}, tags = {}) {
  void sendAnalyticsEvent('basket.remove', details, tags);
}

export function trackClear(details = {}, tags = {}) {
  void sendAnalyticsEvent('clear', details, tags);
}

export function trackQuickPay(details = {}, tags = {}) {
  void sendAnalyticsEvent('quickpay', details, tags);
}

export function trackPayment(details = {}, tags = {}) {
  void sendAnalyticsEvent('payment', details, tags);
}

export function trackConfirmation(details = {}, tags = {}) {
  void sendAnalyticsEvent('confirmation', details, tags);
}

export function trackFailure(details = {}, tags = {}) {
  void sendAnalyticsEvent('failure', details, tags);
}

export function trackSearch(details = {}, tags = {}) {
  void sendAnalyticsEvent('search', details, tags);
}

export function trackAutocomplete(details = {}, tags = {}) {
  void sendAnalyticsEvent('autocomplete', details, tags);
}

export function trackClientError(error, tags = {}) {
  const normalised = normaliseError(error);
  //void sendAnalyticsEvent('error.client', normalised, tags);
}

export function trackApiError(error, endpoint, tags = {}) {
  const normalised = normaliseError(error);
  void sendAnalyticsEvent(
    'error.api',
    {
      endpoint,
      ...normalised,
    },
    tags,
  );
}

export function initAnalytics() {
  try {
    if (!isBrowser()) {
      return;
    }

    if (shouldSkipAnalyticsForAgent()) {
      return;
    }

    if (window.__spaAnalyticsInitialised) {
      return;
    }

    window.__spaAnalyticsInitialised = true;

    getOrCreateSessionId();

    // window.addEventListener('error', event => {
    //   trackClientError(event.error || event.message, {
    //     source: 'window.error',
    //   });
    // });

    window.addEventListener('unhandledrejection', event => {
      trackClientError(event.reason, {
        source: 'window.unhandledrejection',
      });
    });
  } catch (error) {
    safeDebugLog('[analytics] initAnalytics failed', error);
  }
}
